<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('leave.add_leave_type') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submitValid="onSave">
        <oxd-form-row>
          <oxd-input-field
            v-model="leaveType.name"
            :label="$t('general.name')"
            :rules="rules.name"
            required
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-model="leaveType.applyFor"
            type="select"
            :label="$t('Apply For')"
            :options="leaveApplyFor"
            required
            :rules="rules.applyFor"
          />
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-group :classes="{wrapper: '--status-grouped-field'}">
                <template #label>
                  <div class="label-is-entitlement-situational">
                    <oxd-label
                      :label="$t('leave.is_entitlement_situational')"
                    />
                    <oxd-icon-button
                      class="--help"
                      name="exclamation-circle"
                      :with-container="false"
                      @click="onModalOpen"
                    />
                  </div>
                </template>
                <oxd-input-field
                  v-model="leaveType.situational"
                  type="radio"
                  :option-label="$t('general.yes')"
                  :value="true"
                />
                <oxd-input-field
                  v-model="leaveType.situational"
                  type="radio"
                  :option-label="$t('general.no')"
                  :value="false"
                />
              </oxd-input-group>
            </oxd-grid-item>

            <oxd-grid-item>
              <oxd-input-group v-if="!isCompensateType()" :classes="{wrapper: '--status-grouped-field'}" class="auto-add-entitlement">
                <template #label>
                  <div class="label-is-entitlement-situational">
                    <oxd-label
                      :label="$t('Is Auto Add Entitlement?')"
                    />
                  </div>
                </template>
                <oxd-input-field
                  v-model="leaveType.autoAddEntitlement"
                  type="radio"
                  :option-label="$t('general.yes')"
                  :value="true"
                />
                <oxd-input-field
                  :disabled="isAutoAddEntitlement()"
                  v-model="leaveType.autoAddEntitlement"
                  type="radio"
                  :option-label="$t('general.no')"
                  :value="false"
                />
              </oxd-input-group>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-input-field
            v-if="leaveType.autoAddEntitlement && !isCompensateType()"
            v-model="leaveType.noDaysEntitlement"
            :label="$t('Entitlement')"
            :rules="rules.noDaysEntitlement"
          />
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>

      <div class="orangehrm-maintenance-note">
          <div class="orangehrm-maintenance-note-header">
            <oxd-text>Apply For</oxd-text>
          </div>
          <oxd-text>- Remote: Apply for remote working.</oxd-text>
          <oxd-text>- Leave: Apply for normal leave.</oxd-text>
          <oxd-text>- Paid Leave: Apply for paid leave (Select it to apply a separate paid leave calculation formula,
            You should tick "Yes" in <b>Is Auto Add Entitlement</b> field below for its working).</oxd-text>

          <div class="orangehrm-maintenance-note-header">
            <oxd-text>Auto Add Entitlement</oxd-text>
          </div>
          <oxd-text>- Tick "Yes" if you want to automatically add leave days when adding new employees and when entering a new seniority.</oxd-text>

          <div class="orangehrm-maintenance-note-header">
            <oxd-text>Entitlement</oxd-text>
          </div>
          <oxd-text>- If you chosen <b>Is Auto Add Entitlement</b> above, please fill a number of days leave to auto add for employees.</oxd-text>
      </div>

      <entitlement-situational-modal
        v-if="showModal"
        @close="onModalClose"
      ></entitlement-situational-modal>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
  greaterThanOrEqual,
} from '@ohrm/core/util/validation/rules';
import Label from '@ohrm/oxd/core/components/Label/Label';
import EntitlementSituationalModal from '@/orangehrmLeavePlugin/components/EntitlementSituationalModal';

const leaveTypeModel = {
  id: '',
  name: '',
  situational: false,
  autoAddEntitlement: false,
  noDaysEntitlement: 0,
  applyFor: '',
};
const PAID_LEAVE = "PAID_LEAVE";
const REMOTE = "REMOTE";
const COMPENSATE_SENIORITY = "COMPENSATE_SENIORITY";
const COMPENSATE_OTHER = "COMPENSATE_OTHER";

export default {
  components: {
    'oxd-label': Label,
    'entitlement-situational-modal': EntitlementSituationalModal,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/leave-types',
    );
    return {
      http,
    };
  },

  data() {
    return {
      showModal: false,
      isLoading: false,
      leaveType: {...leaveTypeModel},
      rules: {
        name: [required, shouldNotExceedCharLength(50)],
        noDaysEntitlement: [greaterThanOrEqual(1), required],
        applyFor: [required],
      },
      errors: [],
    };
  },

  props: {
    leaveApplyFor: {
      type: Array,
      required: true,
    },
  },

  created() {
    this.isLoading = true;
    this.http
      .getAll()
      .then(response => {
        const {data} = response.data;
        this.rules.name.push(v => {
          const index = data.findIndex(item => item.name == v);
          return index === -1 || this.$t('general.already_exists');
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onSave() {
      this.isLoading = true;
      if (!this.leaveType.autoAddEntitlement) {
        this.leaveType.noDaysEntitlement = 0;
      }
      this.http
        .create({
          name: this.leaveType.name,
          situational: this.leaveType.situational,
          autoAddEntitlement: this.leaveType.autoAddEntitlement,
          noDaysEntitlement: this.leaveType.noDaysEntitlement,
          applyFor: this.leaveType.applyFor.id,
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.leaveType = {...leaveTypeModel};
          this.onCancel();
        });
    },
    onCancel() {
      navigate('/leave/leaveTypeList');
    },
    onModalOpen() {
      this.showModal = true;
    },
    onModalClose() {
      this.showModal = false;
    },
    isAutoAddEntitlement() {
      const isAutoAddEntitlement = this.leaveType.applyFor.id == PAID_LEAVE || this.leaveType.applyFor.id == REMOTE;
      this.leaveType.autoAddEntitlement = isAutoAddEntitlement;
      return isAutoAddEntitlement;
    },
    isCompensateType() {
      const isCompensate = this.leaveType.applyFor.id == COMPENSATE_SENIORITY || this.leaveType.applyFor.id == COMPENSATE_OTHER;
      this.leaveType.autoAddEntitlement = !isCompensate;
      return isCompensate;
    }
  },
};
</script>

<style lang="scss" scoped>
@import './leave-type.scss';
.orangehrm-maintenance-note {
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  padding: 1.2rem;
  border-radius: 1.2rem;
  color: $oxd-input-control-font-color;
  background-color: $oxd-background-light-gray-color;
  font-size: $oxd-input-control-font-size;

  &-header {
    font-weight: bold;
    margin-top: 0.4rem;
    margin-bottom: 0.3rem;
  }
}
</style>
